import { useEffect, useId, useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, Link2, Play, RotateCcw, Shuffle, Square, Undo2, X } from 'lucide-react'
import { changedKeys, PRESETS, type PresetKey } from '../lib/defaults'
import { validateSimulationInputs } from '../lib/monteCarlo'
import { getStrategyMeta, needsTailRiskWarning } from '../lib/strategy'
import type {
  ContractRounding,
  InsolvencyMode,
  IvModel,
  MarginMode,
  SettlementStyle,
  SimulationInputs,
  SizingMode,
  StrategyKind,
  StrikeMethod,
} from '../types'
import { InfoTip } from './InfoTip'

interface Props {
  inputs: SimulationInputs
  running: boolean
  progress: number
  stale: boolean
  open: boolean
  onClose: () => void
  onChange: (patch: Partial<SimulationInputs>) => void
  onRun: () => void
  onCancel: () => void
  onReset: () => void
  onRandomizeSeed: () => void
  onShare: () => void
}

const formatDraft = (value: number) => (Number.isFinite(value) ? String(Number(value.toPrecision(10))) : '')

/**
 * Keeps the raw text while the user types, so clearing the box, typing "-" or
 * "0." never commits a bogus 0. Only parseable, in-range values are committed;
 * anything else is flagged inline and reverted on blur.
 */
function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  suffix,
  help,
  integer,
}: {
  label: string
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  suffix?: string
  help?: string
  integer?: boolean
}) {
  const id = useId()
  const [draft, setDraft] = useState(() => formatDraft(value))
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    if (!focused) setDraft(formatDraft(value))
  }, [value, focused])

  const parsed = draft.trim() === '' ? NaN : Number(draft)
  let problem = ''
  if (!Number.isFinite(parsed)) problem = '请输入数字'
  else if (integer && !Number.isInteger(parsed)) problem = '需为整数'
  else if (min !== undefined && parsed < min) problem = '最小 ' + min
  else if (max !== undefined && parsed > max) problem = '最大 ' + max

  return (
    <label className="field" htmlFor={id}>
      <span className="field__label">
        {label}
        {help && <InfoTip>{help}</InfoTip>}
      </span>
      <span className="field__input-wrap">
        <input
          id={id}
          type="text"
          inputMode={min !== undefined && min >= 0 ? 'decimal' : 'text'}
          autoComplete="off"
          enterKeyHint="done"
          value={draft}
          aria-invalid={problem ? true : undefined}
          aria-describedby={problem ? id + '-err' : undefined}
          data-step={step}
          className={suffix ? 'has-suffix' : undefined}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false)
            setDraft(formatDraft(value))
          }}
          onChange={(event) => {
            const text = event.target.value
            setDraft(text)
            const next = text.trim() === '' ? NaN : Number(text)
            if (!Number.isFinite(next)) return
            if (integer && !Number.isInteger(next)) return
            if ((min !== undefined && next < min) || (max !== undefined && next > max)) return
            onChange(next)
          }}
        />
        {suffix && <span className="field__suffix">{suffix}</span>}
      </span>
      {problem && <span className="field__error" id={id + '-err'} role="alert">{problem}</span>}
    </label>
  )
}

function SelectField<T extends string>({
  label,
  value,
  onChange,
  help,
  children,
}: {
  label: string
  value: T
  onChange: (value: T) => void
  help?: ReactNode
  children: ReactNode
}) {
  return (
    <label className="field">
      <span className="field__label">
        {label}
        {help && <InfoTip>{help}</InfoTip>}
      </span>
      <select value={value} onChange={(event) => onChange(event.target.value as T)}>{children}</select>
    </label>
  )
}

function Section({
  title,
  summary,
  accent,
  defaultOpen = true,
  children,
}: {
  title: string
  summary?: string
  accent?: boolean
  defaultOpen?: boolean
  children: ReactNode
}) {
  return (
    <details className={'parameter-section' + (accent ? ' parameter-section--accent' : '')} open={defaultOpen}>
      <summary>
        <h3>{title}</h3>
        {summary && <span className="parameter-section__summary">{summary}</span>}
        <ChevronDown size={14} aria-hidden="true" />
      </summary>
      <div className="parameter-section__body">{children}</div>
    </details>
  )
}

const FIELD_LABELS: Partial<Record<keyof SimulationInputs, string>> = {
  ivModel: 'IV Model',
  sizingMode: 'Sizing',
  contractRounding: '合约取整',
  skewSlope: 'Skew',
  jumpIntensity: 'Jump λ',
  jumpMean: 'Jump 均值',
  jumpVol: 'Jump vol',
  commissionPerContract: 'Option 佣金',
  optionSlippagePct: 'Option 滑点',
  stockCommissionPerShare: 'Stock 佣金',
  stockSlippageBps: 'Stock 滑点',
  shortBorrowFee: '借券费',
  insolvencyMode: '权益≤0 规则',
  settlementStyle: '结算方式',
  marginMode: '保证金模式',
}

export function ParameterPanel({
  inputs,
  running,
  progress,
  stale,
  open,
  onClose,
  onChange,
  onRun,
  onCancel,
  onReset,
  onRandomizeSeed,
  onShare,
}: Props) {
  const pct = (value: number) => value * 100
  const fromPct = (value: number) => value / 100
  const meta = getStrategyMeta(inputs.strategy)
  const [presetNote, setPresetNote] = useState<{ label: string; changed: string[]; previous: Partial<SimulationInputs> } | null>(null)

  const validationError = useMemo(() => {
    try {
      validateSimulationInputs(inputs)
      return ''
    } catch (error) {
      return error instanceof Error ? error.message : String(error)
    }
  }, [inputs])

  const applyPreset = (key: PresetKey) => {
    const { label, patch } = PRESETS[key]
    const keys = changedKeys(inputs, patch)
    const previous = Object.fromEntries(keys.map((field) => [field, inputs[field]])) as Partial<SimulationInputs>
    setPresetNote({ label, changed: keys.map((field) => FIELD_LABELS[field] ?? field), previous })
    if (keys.length) onChange(patch)
  }

  const costSummary =
    inputs.commissionPerContract || inputs.optionSlippagePct || inputs.stockCommissionPerShare || inputs.stockSlippageBps
      ? '含交易成本'
      : '零成本'
  const jumpSummary = inputs.jumpIntensity > 0 ? 'λ ' + inputs.jumpIntensity + '/年' : '关闭'

  return (
    <aside className={'parameter-panel' + (open ? ' is-open' : '')} id="parameter-panel" aria-label="模拟参数">
      <div className="panel-heading">
        <div>
          <p className="eyebrow">SIMULATION INPUTS</p>
          <h2>参数</h2>
        </div>
        <div className="panel-heading__actions">
          <button className="icon-button" onClick={onShare} title="复制可分享链接" aria-label="复制可分享链接">
            <Link2 size={16} />
          </button>
          <button className="icon-button" onClick={onReset} title="恢复默认参数" aria-label="恢复默认参数">
            <RotateCcw size={16} />
          </button>
          <button className="icon-button panel-close" onClick={onClose} aria-label="关闭参数面板">
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="parameter-panel__scroll">
        <Section title="模型预设" accent>
          <div className="preset-row">
            {(Object.keys(PRESETS) as PresetKey[]).map((key) => (
              <button key={key} className="play-small" onClick={() => applyPreset(key)}>{PRESETS[key].label}</button>
            ))}
          </div>
          {presetNote ? (
            <p className="preset-note" role="status">
              {presetNote.changed.length
                ? <>已应用 {presetNote.label}，修改 {presetNote.changed.length} 项：{presetNote.changed.join('、')}。</>
                : <>当前参数已与 {presetNote.label} 一致。</>}
              {presetNote.changed.length > 0 && (
                <button className="link-button" onClick={() => { onChange(presetNote.previous); setPresetNote(null) }}>
                  <Undo2 size={12} /> 撤销
                </button>
              )}
            </p>
          ) : (
            <p className="micro-note">预设只修改模型复杂度相关参数，不改变策略、价格、期限和 Monte Carlo 路径数。</p>
          )}
        </Section>

        <Section title="策略结构" accent summary={meta.shortLabel}>
          <SelectField
            label="Strategy"
            value={inputs.strategy}
            onChange={(strategy: StrategyKind) => onChange({ strategy })}
            help="策略由股票腿与一个或多个 Call/Put 腿组合。多腿策略的每个期权腿独立定价、MTM 与结算。"
          >
            <optgroup label="Stock + Option">
              <option value="covered_call">Covered Call</option>
              <option value="protective_put">Protective Put</option>
              <option value="covered_put">Covered Put</option>
              <option value="short_stock_short_call">Short Stock + Short Call</option>
              <option value="collar">Collar · Long Stock + Long Put + Short Call</option>
            </optgroup>
            <optgroup label="Option Only">
              <option value="naked_call">Naked Short Call</option>
              <option value="naked_put">Naked Short Put</option>
              <option value="cash_secured_put">Cash-Secured Put</option>
              <option value="long_straddle">Long Straddle</option>
              <option value="short_straddle">Short Straddle</option>
              <option value="risk_reversal">Risk Reversal · Long Call + Short Put</option>
              <option value="reverse_risk_reversal">Reverse Risk Reversal · Short Call + Long Put</option>
            </optgroup>
          </SelectField>
          <div className={needsTailRiskWarning(inputs.strategy) ? 'risk-note risk-note--danger' : 'risk-note'}>
            <strong>{meta.label}</strong>
            <span>{meta.description}</span>
            <small>{meta.risk}</small>
          </div>
        </Section>

        <Section title="标的资产" summary={'S ' + inputs.initialPrice + ' · ' + inputs.horizonYears + '年'}>
          <div className="field-grid">
            <NumberField label="初始价格" value={inputs.initialPrice} min={0.01} onChange={(v) => onChange({ initialPrice: v })} />
            <NumberField label="投入资本" value={inputs.capital} min={100} step={1000} onChange={(v) => onChange({ capital: v })} help="初始经济权益；不是券商保证金要求。" />
            <NumberField label="真实世界 drift μ" value={pct(inputs.drift)} suffix="%" step={1} onChange={(v) => onChange({ drift: fromPct(v) })} help="只进入标的价格路径，不进入 Black-Scholes 定价；这里是除息价格漂移，不包含股息收益。" />
            <NumberField label="模拟期限" value={inputs.horizonYears} suffix="年" min={0.08} max={5} step={0.25} onChange={(v) => onChange({ horizonYears: v })} />
          </div>
        </Section>

        <Section title="波动率" accent summary={'IV ' + pct(inputs.impliedVol).toFixed(0) + '% · σ ' + pct(inputs.realizedVol).toFixed(0) + '%'}>
          <div className="field-grid">
            <NumberField label="Diffusion Vol σ" value={pct(inputs.realizedVol)} suffix="%" min={0} max={150} onChange={(v) => onChange({ realizedVol: fromPct(v) })} help="控制连续扩散部分。开启 Jump 后，总实现波动率还会包含跳跃方差。" />
            <NumberField label="Entry IV" value={pct(inputs.impliedVol)} suffix="%" min={1} max={200} onChange={(v) => onChange({ impliedVol: fromPct(v) })} />
            <SelectField label="IV Model" value={inputs.ivModel} onChange={(ivModel: IvModel) => onChange({ ivModel })}>
              <option value="constant">Constant</option>
              <option value="mean_reverting">Mean Reverting</option>
              <option value="stress_regime">Stress Regime</option>
            </SelectField>
            <NumberField label="长期 IV" value={pct(inputs.longRunIv)} suffix="%" min={1} max={200} onChange={(v) => onChange({ longRunIv: fromPct(v) })} />
            {inputs.ivModel !== 'constant' && (
              <NumberField label="IV 回归速度 κ" value={inputs.ivMeanReversion} min={0} max={30} step={0.5} onChange={(v) => onChange({ ivMeanReversion: v })} />
            )}
            {inputs.ivModel === 'stress_regime' && (
              <>
                <NumberField label="Stress 触发单日跌幅" value={pct(inputs.stressTriggerReturn)} suffix="%" min={-50} max={0} step={0.5} onChange={(v) => onChange({ stressTriggerReturn: fromPct(v) })} />
                <NumberField label="Stress IV" value={pct(inputs.stressIv)} suffix="%" min={1} max={300} onChange={(v) => onChange({ stressIv: fromPct(v) })} />
              </>
            )}
            <NumberField label="Skew slope" value={inputs.skewSlope} step={0.05} onChange={(v) => onChange({ skewSlope: v })} help="IV(K)=baseIV+slope×ln(K/F)。负值使 downside put IV 更高。" />
          </div>
          <div className="vrp-strip">
            <span>Entry IV − RV</span>
            <strong className={inputs.impliedVol >= inputs.realizedVol ? 'positive' : 'negative'}>
              {inputs.impliedVol >= inputs.realizedVol ? '+' : ''}{((inputs.impliedVol - inputs.realizedVol) * 100).toFixed(1)} vol pts
            </strong>
          </div>
        </Section>

        <Section title="Option Legs 规则" summary={inputs.dte + 'D · ' + inputs.strikeMethod.toUpperCase()}>
          <div className="field-grid">
            <NumberField label="期限 DTE" value={inputs.dte} suffix="天" min={1} max={365} integer onChange={(v) => onChange({ dte: v })} help="日历 DTE 会一次性转换为交易日周期，之后 entry 与 MTM 使用同一 252-day 时钟。" />
            <SelectField label="执行价方式" value={inputs.strikeMethod} onChange={(strikeMethod: StrikeMethod) => onChange({ strikeMethod })}>
              <option value="delta">BS Spot |Delta|</option>
              <option value="otm">% OTM</option>
              <option value="atm">ATM</option>
              <option value="fixed">固定 Strike</option>
            </SelectField>
            {inputs.strikeMethod === 'delta' && (
              <NumberField label="Target |Delta|" value={inputs.targetDelta} min={0.01} max={0.5} step={0.05} onChange={(v) => onChange({ targetDelta: v })} />
            )}
            {inputs.strikeMethod === 'otm' && (
              <NumberField label="OTM" value={pct(inputs.otmPct)} suffix="%" min={0} max={99} step={0.5} onChange={(v) => onChange({ otmPct: fromPct(v) })} />
            )}
            {inputs.strikeMethod === 'fixed' && meta.optionLegs.some((leg) => leg.optionType === 'call') && (
              <NumberField
                label={meta.optionLegs.some((leg) => leg.optionType === 'put') ? '固定 Strike · Call' : '固定 Strike'}
                value={inputs.fixedCallStrike}
                min={0.01}
                onChange={(v) => onChange({ fixedCallStrike: v })}
                help="仅用于 Call 腿；与 Put 腿的固定 Strike 相互独立，可用于构造非对称 Collar/价差，两者设为相同值则退化为同执行价组合。"
              />
            )}
            {inputs.strikeMethod === 'fixed' && meta.optionLegs.some((leg) => leg.optionType === 'put') && (
              <NumberField
                label={meta.optionLegs.some((leg) => leg.optionType === 'call') ? '固定 Strike · Put' : '固定 Strike'}
                value={inputs.fixedPutStrike}
                min={0.01}
                onChange={(v) => onChange({ fixedPutStrike: v })}
                help="仅用于 Put 腿；与 Call 腿的固定 Strike 相互独立，可用于构造非对称 Collar/价差，两者设为相同值则退化为同执行价组合。"
              />
            )}
            <NumberField label="合约乘数" value={inputs.contractMultiplier} min={1} integer onChange={(v) => onChange({ contractMultiplier: v })} />
            <SelectField label="合约取整" value={inputs.contractRounding} onChange={(contractRounding: ContractRounding) => onChange({ contractRounding })}>
              <option value="fractional">Fractional research</option>
              <option value="floor">Floor</option>
              <option value="nearest">Nearest</option>
            </SelectField>
            <NumberField
              label="名义本金 / Equity"
              value={inputs.notionalMultiple}
              suffix="×"
              min={0.1}
              max={10}
              step={0.1}
              onChange={(v) => onChange({ notionalMultiple: v })}
              help={inputs.strategy === 'cash_secured_put'
                ? 'CSP 会在每次开仓时按 Strike × shares 强制受可用现金约束；即使这里大于 1×，也不会突破现金担保上限。'
                : '控制股票/期权名义本金相对初始权益的倍数。'}
            />
            <SelectField label="Sizing" value={inputs.sizingMode} onChange={(sizingMode: SizingMode) => onChange({ sizingMode })}>
              <option value="fixed_units">Fixed units from Day 0</option>
              <option value="equity_rebalance">Rebalance each roll</option>
            </SelectField>
            {inputs.sizingMode === 'equity_rebalance' && (
              <SelectField
                label="权益≤0后的规则"
                value={inputs.insolvencyMode}
                onChange={(insolvencyMode: InsolvencyMode) => onChange({ insolvencyMode })}
                help="继续既有名义风险更适合研究 naked short 的经济尾部；停止新增风险相当于显式的风险控制规则，不代表券商强平。"
              >
                <option value="continue_fixed_units">继续既有合约数（尾部研究）</option>
                <option value="stop_new_risk">停止新开风险（风险控制）</option>
              </SelectField>
            )}
            <SelectField
              label="结算方式"
              value={inputs.settlementStyle}
              onChange={(settlementStyle: SettlementStyle) => onChange({ settlementStyle })}
              help="Cash 使用现金等价到期结算；Physical 在到期日按执行价交割股票，并立即恢复原策略股票腿。暂不模拟 American early exercise。"
            >
              <option value="cash">Cash settlement</option>
              <option value="physical_expiry">Physical delivery at expiry</option>
            </SelectField>
            <SelectField
              label="保证金模式"
              value={inputs.marginMode}
              onChange={(marginMode: MarginMode) => onChange({ marginMode })}
              help="Reg-T Approx 使用 Cboe 公布的 uncovered equity option maintenance 公式做研究级近似；触发时平掉策略风险并留在现金。不是券商正式保证金引擎。"
            >
              <option value="off">Off · 纯经济 P&amp;L</option>
              <option value="reg_t_approx">Reg-T Approx + liquidation</option>
            </SelectField>
          </div>
        </Section>

        <Section title="资金、借券与成本" summary={costSummary} defaultOpen={false}>
          <div className="field-grid">
            <NumberField label="BS Risk-free rate" value={pct(inputs.riskFreeRate)} suffix="%" step={0.25} onChange={(v) => onChange({ riskFreeRate: fromPct(v) })} />
            <NumberField label="Cash yield" value={pct(inputs.cashYield)} suffix="%" step={0.25} onChange={(v) => onChange({ cashYield: fromPct(v) })} />
            <NumberField label="Funding rate" value={pct(inputs.fundingRate)} suffix="%" step={0.25} onChange={(v) => onChange({ fundingRate: fromPct(v) })} help="负现金融资成本；应不低于 Cash yield。" />
            <NumberField label="股息率" value={pct(inputs.dividendYield)} suffix="%" min={0} step={0.25} onChange={(v) => onChange({ dividendYield: fromPct(v) })} />
            <NumberField label="Short borrow fee" value={pct(inputs.shortBorrowFee)} suffix="%" min={0} step={0.25} onChange={(v) => onChange({ shortBorrowFee: fromPct(v) })} />
            <NumberField label="Short proceeds rebate" value={pct(inputs.shortProceedsRebate)} suffix="%" step={0.25} onChange={(v) => onChange({ shortProceedsRebate: fromPct(v) })} />
            <NumberField label="Option 佣金 / contract" value={inputs.commissionPerContract} min={0} step={0.05} onChange={(v) => onChange({ commissionPerContract: v })} />
            <NumberField label="Option slippage" value={pct(inputs.optionSlippagePct)} suffix="%" min={0} max={100} step={0.5} onChange={(v) => onChange({ optionSlippagePct: fromPct(v) })} help="按权利金百分比计算的滑点。" />
            <NumberField label="Stock 佣金 / share" value={inputs.stockCommissionPerShare} min={0} step={0.001} onChange={(v) => onChange({ stockCommissionPerShare: v })} />
            <NumberField label="Stock slippage" value={inputs.stockSlippageBps} suffix="bps" min={0} step={0.5} onChange={(v) => onChange({ stockSlippageBps: v })} />
          </div>
        </Section>

        <Section title="Jump / Stress" summary={jumpSummary} defaultOpen={false}>
          <div className="field-grid">
            <NumberField label="Jump intensity λ / year" value={inputs.jumpIntensity} min={0} max={20} step={0.25} onChange={(v) => onChange({ jumpIntensity: v })} />
            <NumberField label="平均 log jump" value={pct(inputs.jumpMean)} suffix="%" step={1} onChange={(v) => onChange({ jumpMean: fromPct(v) })} />
            <NumberField label="Jump vol" value={pct(inputs.jumpVol)} suffix="%" min={0} step={1} onChange={(v) => onChange({ jumpVol: fromPct(v) })} />
          </div>
        </Section>

        <Section title="计算" summary={inputs.paths.toLocaleString() + ' 条路径'}>
          <div className="seed-row">
            <NumberField label="模拟路径" value={inputs.paths} min={100} max={50000} step={500} integer onChange={(v) => onChange({ paths: v })} />
            <span />
            <NumberField label="Random Seed" value={inputs.seed} min={1} step={1} integer onChange={(v) => onChange({ seed: v })} />
            <button className="icon-button" onClick={onRandomizeSeed} title="更换随机种子" aria-label="更换随机种子"><Shuffle size={16} /></button>
          </div>
        </Section>

        <p className="panel-footnote">
          研究模型：可选到期 physical delivery 与 Reg-T 风格保证金近似；仍不模拟 broker-specific portfolio margin、American early exercise、离散分红或真实 order book。
        </p>
      </div>

      <div className="panel-run">
        {validationError && <p className="panel-validation" role="alert">参数无效：{validationError}</p>}
        {!validationError && stale && !running && <p className="panel-stale">参数已修改，结果尚未更新</p>}
        {running ? (
          <button className="run-button run-button--cancel" onClick={onCancel}>
            <Square size={14} fill="currentColor" /> 取消 · {progress}%
          </button>
        ) : (
          <button className="run-button" onClick={onRun} disabled={Boolean(validationError)} title="Ctrl/⌘ + Enter">
            <Play size={16} fill="currentColor" />
            {stale ? '重新运行模拟' : '运行模拟'}
          </button>
        )}
        {running && <div className="progress-track" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}><span style={{ width: progress + '%' }} /></div>}
      </div>
    </aside>
  )
}
